import { useState } from 'react';
import { ListChecks, Loader2, Package } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

// Types
import type { ChecklistItem, ModalType, SortColumn, SortDirection, ParsedItem } from './types';

// Hooks
import {
  useChecklists,
  useChecklist,
  useProcessedItems,
  useAllActiveOnWatchlist,
  useChecklistStats,
} from './hooks/useChecklistData';
import { useChecklistMutations } from './hooks/useChecklistMutations';

// Utils
import { parseIKEAText } from './utils/parser';
import { getNextStatus } from './utils/helpers';

// Components
import { ChecklistsHero } from './components/ChecklistsHero';
import { ChecklistSidebar } from './components/ChecklistSidebar';
import { ChecklistHeader } from './components/ChecklistHeader';
import { ChecklistTable } from './components/ChecklistTable';
import { CompletedAccordion } from './components/CompletedAccordion';

// Modals
import {
  CreateChecklistModal,
  AddItemModal,
  ImportModal,
  ImportPreviewModal,
  ItemDetailModal,
  PurchaseModal,
} from './components/modals';

export default function Checklists() {
  const { t } = useLanguage();

  // Core state
  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalType>('none');
  const [filter, setFilter] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Import state
  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([]);
  const [importAfterCreate, setImportAfterCreate] = useState(false);

  // Item detail state
  const [selectedItem, setSelectedItem] = useState<ChecklistItem | null>(null);

  // Data fetching
  const { data: checklists = [], isLoading: loadingChecklists } = useChecklists();
  const { data: selectedChecklist, isLoading: loadingChecklist } = useChecklist(selectedChecklistId);

  // Process items with filter and sort
  const processedItems = useProcessedItems(
    selectedChecklist?.items,
    filter,
    sortColumn,
    sortDirection
  );

  // Check if all items are on watchlist
  const allActiveOnWatchlist = useAllActiveOnWatchlist(processedItems.active);

  // Calculate stats for hero
  const stats = useChecklistStats(checklists, selectedChecklist?.items);

  // Mutations with callbacks
  const mutations = useChecklistMutations(selectedChecklistId, {
    onCreateSuccess: (data) => {
      setModal('none');
      if (importAfterCreate && data?.id) {
        setSelectedChecklistId(data.id);
        setTimeout(() => setModal('import'), 300);
      }
      setImportAfterCreate(false);
    },
    onDeleteSuccess: () => {
      setSelectedChecklistId(null);
    },
    onAddItemSuccess: () => {
      setModal('none');
    },
    onDeleteItemSuccess: () => {
      setModal('none');
      setSelectedItem(null);
    },
    onImportSuccess: (data) => {
      setModal('none');
      setParsedItems([]);
      alert(t('checklists.importedCount', `Imported ${data?.imported || 0} items`));
    },
    onAddAllToWatchlistSuccess: (data) => {
      alert(t('checklists.addedToWatchlistCount', `Added ${data?.added || 0} items to watchlist`));
    },
  });

  // Handlers
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCreateChecklist = (name: string, description: string | undefined, shouldImportAfter: boolean) => {
    setImportAfterCreate(shouldImportAfter);
    mutations.createMutation.mutate({ name, description });
  };

  const handleAddItem = (name: string, code: string | undefined, quantity: number) => {
    if (!selectedChecklistId) return;
    mutations.addItemMutation.mutate({
      checklistId: selectedChecklistId,
      item: {
        productCode: code,
        itemName: name,
        quantityNeeded: quantity,
      },
    });
  };

  const handleParseImport = (text: string) => {
    const items = parseIKEAText(text);
    if (items.length > 0) {
      setParsedItems(items);
      setModal('import-preview');
    } else {
      alert(t('checklists.noProductCodesFound', 'No IKEA product codes found. Make sure the text contains codes like 123.456.78'));
    }
  };

  const handleConfirmImport = () => {
    if (!selectedChecklistId || parsedItems.length === 0) return;
    mutations.importMutation.mutate({ checklistId: selectedChecklistId, items: parsedItems });
  };

  const handleItemClick = (item: ChecklistItem) => {
    setSelectedItem(item);
    setModal('item-detail');
  };

  const handleStartPurchase = () => {
    setModal('purchase');
  };

  const handleConfirmPurchase = (quantity: number) => {
    if (!selectedChecklistId || !selectedItem) return;
    const newPurchased = (selectedItem.quantityPurchased || 0) + quantity;
    const isComplete = newPurchased >= (selectedItem.quantityNeeded || 1);

    mutations.updateItemMutation.mutate({
      checklistId: selectedChecklistId,
      itemId: selectedItem.id,
      data: {
        quantityPurchased: newPurchased,
        status: isComplete ? 'purchased' : selectedItem.status,
      },
    });
    setModal('none');
    setSelectedItem(null);
  };

  const handleToggleWatchlist = (itemId: string) => {
    if (!selectedChecklistId) return;
    mutations.toggleWatchlistMutation.mutate({
      checklistId: selectedChecklistId,
      itemId,
    });
  };

  const handleToggleStatus = () => {
    if (!selectedChecklistId || !selectedItem) return;
    mutations.updateItemMutation.mutate({
      checklistId: selectedChecklistId,
      itemId: selectedItem.id,
      data: { status: getNextStatus(selectedItem.status) },
    });
  };

  const handleDeleteItem = () => {
    if (!selectedChecklistId || !selectedItem) return;
    if (confirm(t('checklists.confirmDeleteItem', 'Remove this item from the checklist?'))) {
      mutations.deleteItemMutation.mutate({
        checklistId: selectedChecklistId,
        itemId: selectedItem.id,
      });
    }
  };

  const handleDeleteChecklist = () => {
    if (!selectedChecklist) return;
    if (confirm(t('checklists.confirmDelete', 'Are you sure you want to delete this checklist?'))) {
      mutations.deleteMutation.mutate(selectedChecklist.id);
    }
  };

  // Loading state
  if (loadingChecklists) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-6">
      {/* Hero Section */}
      <ChecklistsHero
        stats={stats}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        onCreateClick={() => setModal('create')}
      />

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <ChecklistSidebar
              checklists={checklists}
              selectedId={selectedChecklistId}
              onSelect={setSelectedChecklistId}
              onCreateClick={() => setModal('create')}
              isLoading={loadingChecklists}
            />
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            {!selectedChecklistId ? (
              <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <ListChecks className="w-8 h-8 text-gray-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('checklists.selectChecklist', 'Select a checklist')}
                </h2>
                <p className="text-gray-600 mt-2 max-w-sm mx-auto">
                  {t('checklists.selectChecklistDescription', 'Choose a checklist from the sidebar or create a new one to get started')}
                </p>
              </div>
            ) : loadingChecklist ? (
              <div className="bg-white border border-gray-200 rounded-2xl p-12 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : selectedChecklist ? (
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                {/* Header */}
                <ChecklistHeader
                  checklist={selectedChecklist}
                  filter={filter}
                  onFilterChange={setFilter}
                  onDelete={handleDeleteChecklist}
                  onAddItem={() => setModal('add-item')}
                  onImport={() => setModal('import')}
                  onWatchAll={() => mutations.addAllToWatchlistMutation.mutate(selectedChecklist.id)}
                  allOnWatchlist={allActiveOnWatchlist}
                  isWatchAllPending={mutations.addAllToWatchlistMutation.isPending}
                  isDeleting={mutations.deleteMutation.isPending}
                />

                {/* Items */}
                {selectedChecklist.items.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <Package className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-gray-600 mb-4">
                      {t('checklists.noItems', 'No items in this checklist')}
                    </p>
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => setModal('add-item')}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        {t('checklists.addItem', 'Add Item')}
                      </button>
                      <button
                        onClick={() => setModal('import')}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        {t('checklists.importFromPDF', 'Import from PDF')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Active Items Section */}
                    {processedItems.active.length > 0 && (
                      <div>
                        <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase border-b border-gray-100">
                          {t('checklists.active', 'Active')} ({processedItems.active.length})
                        </div>
                        <ChecklistTable
                          items={processedItems.active}
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          onItemClick={handleItemClick}
                          onToggleWatchlist={handleToggleWatchlist}
                          checklistId={selectedChecklist.id}
                        />
                      </div>
                    )}

                    {/* Completed Items Accordion */}
                    <CompletedAccordion
                      items={processedItems.completed}
                      onItemClick={handleItemClick}
                    />
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateChecklistModal
        isOpen={modal === 'create'}
        onClose={() => setModal('none')}
        onSubmit={handleCreateChecklist}
        isPending={mutations.createMutation.isPending}
      />

      <AddItemModal
        isOpen={modal === 'add-item'}
        onClose={() => setModal('none')}
        onSubmit={handleAddItem}
        isPending={mutations.addItemMutation.isPending}
      />

      <ImportModal
        isOpen={modal === 'import'}
        onClose={() => setModal('none')}
        onParse={handleParseImport}
      />

      <ImportPreviewModal
        isOpen={modal === 'import-preview'}
        onClose={() => { setModal('none'); setParsedItems([]); }}
        onBack={() => setModal('import')}
        onConfirm={handleConfirmImport}
        items={parsedItems}
        isPending={mutations.importMutation.isPending}
      />

      <ItemDetailModal
        isOpen={modal === 'item-detail'}
        onClose={() => { setModal('none'); setSelectedItem(null); }}
        item={selectedItem}
        onToggleWatchlist={() => selectedItem && handleToggleWatchlist(selectedItem.id)}
        onStartPurchase={handleStartPurchase}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDeleteItem}
      />

      {selectedItem && (
        <PurchaseModal
          key={selectedItem.id}
          isOpen={modal === 'purchase'}
          onClose={() => setModal('item-detail')}
          item={selectedItem}
          onConfirm={handleConfirmPurchase}
          isPending={mutations.updateItemMutation.isPending}
        />
      )}
    </div>
  );
}
